import React, { Component } from "react";
import { PropTypes } from "prop-types";
import { Button } from "@zendesk/self-service-components";
import { Prompt } from "react-router-dom";
import Chrome from "@zendesk/knowledge-chrome/lib/Chrome";
import t from "@zendesk/client-i18n-tools";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import isEqual from "lodash/isEqual";
import { getCurrentBrandId } from "@zendesk/knowledge-context";
import Wizard from "@zendesk/knowledge-components/lib/Wizard";
import {
  CREATE_STEP_SETUP,
  CREATE_STEP_SEGMENT,
  CREATE_STEP_REVIEW,
  USER_TYPES,
} from "../../constants";
import { getUserSegmentsUrl } from "utils/urls";
import * as actions from "../../actions/userSegment";
import Notifications from "../Notifications";
import UserList from "../UserSegment/components/UserList";
import SetupStep from "./components/SetupStep";
import SegmentStep from "./components/SegmentStep";
import ReviewStep from "./components/ReviewStep";
import styles from "./index.css";

export class NewUserSegment extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentStep: CREATE_STEP_SETUP,
    };

    this.onNext = this.onNext.bind(this);
    this.onCreate = this.onCreate.bind(this);
    this.onBack = this.onBack.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onBeforeUnload = this.onBeforeUnload.bind(this);
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
  }

  componentWillUnmount() {
    const { resetUserSegment } = this.props;

    resetUserSegment();
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  }

  onBeforeUnload(e) {
    const { hasChanges } = this.props;

    if (hasChanges) {
      const message = t(
        "knowledge-user-segments.userSegment.unsavedLeaveConfirmation"
      );

      e.returnValue = message;
      return message;
    }
  }

  onBack() {
    const { currentStep } = this.state;
    const { resetUserSegmentAttributes } = this.props;
    let nextStep;

    switch (currentStep) {
      case CREATE_STEP_SEGMENT:
        nextStep = CREATE_STEP_SETUP;
        resetUserSegmentAttributes();
        break;
      case CREATE_STEP_REVIEW:
        nextStep = CREATE_STEP_SEGMENT;
        break;
      default:
        nextStep = CREATE_STEP_SETUP;
        break;
    }

    this.setState({
      currentStep: nextStep,
    });
  }

  onCancel() {
    const { cancelNewUserSegment } = this.props;
    cancelNewUserSegment();
  }

  onCreate() {
    const { createUserSegment } = this.props;
    createUserSegment();
  }

  onNext() {
    const { currentStep } = this.state;
    let nextStep;

    switch (currentStep) {
      case CREATE_STEP_SETUP:
        nextStep = CREATE_STEP_SEGMENT;
        break;
      case CREATE_STEP_SEGMENT:
        nextStep = CREATE_STEP_REVIEW;
        break;
      default:
        nextStep = CREATE_STEP_SETUP;
        break;
    }

    this.setState({
      currentStep: nextStep,
    });
  }

  renderStep() {
    const { currentStep } = this.state;

    switch (currentStep) {
      case CREATE_STEP_SETUP:
        return <SetupStep />;
      case CREATE_STEP_SEGMENT:
        return <SegmentStep />;
      case CREATE_STEP_REVIEW:
        return <ReviewStep />;
      default:
        return <SetupStep />;
    }
  }

  render() {
    const { name, userType, hasChanges } = this.props;
    const { currentStep } = this.state;

    return (
      <Chrome
        dir={t.dir}
        brandId={getCurrentBrandId()}
        topNav={
          <Chrome.Breadcrumbs>
            <Chrome.Breadcrumbs.Item>
              <a href={getUserSegmentsUrl()}>
                {t("knowledge-user-segments.userPermissions")}
              </a>
            </Chrome.Breadcrumbs.Item>
            <Chrome.Breadcrumbs.Separator />
            <Chrome.Breadcrumbs.Item>
              <a href={getUserSegmentsUrl()}>
                {t("knowledge-user-segments.name")}
              </a>
            </Chrome.Breadcrumbs.Item>
            <Chrome.Breadcrumbs.Separator />
            <Chrome.Breadcrumbs.Item>
              {t("knowledge-user-segments.wizard.pageTitle")}
            </Chrome.Breadcrumbs.Item>
          </Chrome.Breadcrumbs>
        }
      >
        <div className={styles.wrp}>
          <Notifications />

          <Prompt
            when={hasChanges}
            message={t(
              "knowledge-user-segments.userSegment.unsavedLeaveConfirmation"
            )}
          />
          <div className={styles.contentWrapper}>
            <div className={styles.contentView}>
              <h1 className={styles.pageTitle}>
                {t("knowledge-user-segments.wizard.pageTitle")}
              </h1>

              <div className={styles.primaryContent}>
                <div className={styles.wizardWrapper}>
                  <Wizard dir={t.dir}>
                    <Wizard.Step
                      active={currentStep === CREATE_STEP_SETUP}
                      label={t("knowledge-user-segments.wizard.setupStep")}
                      completed={[
                        CREATE_STEP_REVIEW,
                        CREATE_STEP_SEGMENT,
                      ].includes(currentStep)}
                    />
                    <Wizard.Step
                      active={currentStep === CREATE_STEP_SEGMENT}
                      label={t("knowledge-user-segments.wizard.segmentStep")}
                      completed={currentStep === CREATE_STEP_REVIEW}
                    />
                    <Wizard.Step
                      active={currentStep === CREATE_STEP_REVIEW}
                      label={t("knowledge-user-segments.wizard.reviewStep")}
                      completed={false}
                    />
                  </Wizard>
                </div>

                {this.renderStep()}
              </div>
            </div>

            {currentStep === CREATE_STEP_SEGMENT && (
              <div className={styles.userList}>
                <div className={styles.contentView}>
                  <UserList />
                </div>
              </div>
            )}
          </div>

          <div className={styles.footer} data-test-id="footer-bar">
            {currentStep === CREATE_STEP_SETUP ? (
              <Button onClick={this.onCancel} testId="cancel-button">
                {t("knowledge-user-segments.wizard.cancel")}
              </Button>
            ) : (
              <Button onClick={this.onBack} testId="back-button">
                {t("knowledge-user-segments.wizard.back")}
              </Button>
            )}

            {currentStep === CREATE_STEP_REVIEW ? (
              <Button
                testId="create-button"
                disabled={name.length === 0}
                type="primary"
                className={styles.nextButton}
                onClick={this.onCreate}
              >
                {t("knowledge-user-segments.wizard.create")}
              </Button>
            ) : (
              <Button
                testId={`next-button-${currentStep}`}
                disabled={name.length === 0 || !USER_TYPES.includes(userType)}
                type="primary"
                className={styles.nextButton}
                onClick={this.onNext}
              >
                {t("knowledge-user-segments.wizard.next")}
              </Button>
            )}
          </div>
        </div>
      </Chrome>
    );
  }
}

NewUserSegment.propTypes = {
  name: PropTypes.string,
  userType: PropTypes.oneOf(USER_TYPES),
  hasChanges: PropTypes.bool,
  resetUserSegment: PropTypes.func,
  createUserSegment: PropTypes.func,
  cancelNewUserSegment: PropTypes.func,
  resetUserSegmentAttributes: PropTypes.func,
};

NewUserSegment.defaultProps = {
  hasChanges: false,
  type: "edit",
  name: "",
  userType: null,
  builtIn: false,
  isFetchingOrganizations: false,
  isFetchingGroups: false,
};

const mapStateToProps = ({ userSegment }, ownProps) => ({
  name: userSegment.name,
  userType: userSegment.userType,
  hasChanges:
    !isEqual(userSegment.savedTags, userSegment.tags) ||
    !isEqual(userSegment.savedOrganizations, userSegment.organizations) ||
    !isEqual(userSegment.savedGroups, userSegment.groups) ||
    userSegment.savedName !== userSegment.name,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actions }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(NewUserSegment);



// WEBPACK FOOTER //
// ./src/components/NewUserSegment/index.js